import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import { Lock, Users, Upload } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FileType, FolderType } from '../../../../types';
import { pushModal } from '../../../globalModals/globalModalsSlice';
import FileActionMenu from '../FileActionMenu';
import { RootState } from '../../../../reducers';
import DefaultThumbnail from '../../../../assets/recording/WhiteThumbnail.svg';
import { useCachedThumbnail } from '../../../../hooks/useCachedThumbnail';
import { Checkbox } from '../../../../components/ui/checkbox';
import { TableCell, TableRow } from '../../../../components/ui/table';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';

interface FileListItemProps {
  id?: string;
  file: FileType;
  isSelected: boolean;
  selectMode?: boolean;
  onSelect?: (file: FileType) => void;
  fileUrl: string;
}

const formatDuration = (seconds: number | undefined): string => {
  if (typeof seconds !== 'number') return '0:00';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return hours > 0
    ? `${hours}:${minutes
        .toString()
        .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    : `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Minimalist import processing row
const ProcessingListItem = ({ name }: { name: string }) => {
  const { folderId } = useParams<{ folderId: string }>();
  const isInRecentsView = folderId === 'recents';

  return (
    <TableRow className="group transition-opacity duration-300 opacity-50 hover:opacity-70">
      <TableCell className="h-14 w-[45%] min-w-[250px] pl-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded bg-muted flex-shrink-0 flex items-center justify-center">
            <Upload className="h-3.5 w-3.5 text-muted-foreground/70" />
          </div>
          <div className="flex flex-col gap-1.5 min-w-0 max-w-0 flex-1">
            <p
              className="text-sm font-medium text-foreground truncate"
              title={name}
            >
              {name}
            </p>
            <div className="h-0.5 w-full bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-muted-foreground/20 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-center w-[70px]">
        <span className="text-xs text-muted-foreground">Processing</span>
      </TableCell>
      {isInRecentsView && (
        <TableCell className="hidden sm:table-cell w-[100px]" />
      )}
      <TableCell className="hidden md:table-cell w-[80px]" />
      <TableCell className="w-[60px]" />
      <TableCell className="w-[44px]" />
    </TableRow>
  );
};

const FileListItem = React.memo(
  ({ file, fileUrl, isSelected, selectMode, onSelect }: FileListItemProps) => {
    const { name, thumbUrl, createdAt, owner, duration, parentId, type, _id } =
      file;
    const { name: ownerName } = owner || { name: '' };
    const [showMenu, setShowMenu] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const longPressTimer = useRef<ReturnType<typeof setTimeout>>();
    const touchStartTime = useRef<number>(0);
    const [isMobile, setIsMobile] = useState(false);

    // Use cached thumbnail hook
    const { thumbnailSrc, isLoading, handleLoad, handleError } =
      useCachedThumbnail({
        fileId: _id,
        thumbnailUrl: thumbUrl,
        fallbackUrl: DefaultThumbnail,
      });

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 640);
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);
    const dispatch = useDispatch();
    const { folderId } = useParams<{ folderId: string }>();
    const isInRecentsView = folderId === 'recents';

    // Check if the file type is Import
    const isImportedFile = type === 'Import';

    const handleTouchStart = () => {
      touchStartTime.current = Date.now();
      longPressTimer.current = setTimeout(() => {
        setShowMenu(true);
      }, 500);
    };

    const handleTouchEnd = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };

    const handleTouchMove = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };

    const parentFolder = useSelector((state: RootState) => {
      return state.library.folders.data.find(
        (folder: FolderType) => folder._id === parentId,
      );
    });

    const handleMoveClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(pushModal({ type: 'move-file-modal', files: [file] }));
      },
      [dispatch, file],
    );

    const handleSelectionClick = (e: React.MouseEvent) => {
      if (onSelect) {
        e.preventDefault();
        e.stopPropagation();
        onSelect(file);
      }
    };

    const handleCheckboxChange = () => {
      if (selectMode && onSelect) {
        onSelect(file);
      }
    };

    if (isImportedFile) {
      return <ProcessingListItem name={name} />;
    }

    // Normal item display for non-processing files
    return (
      <TableRow
        data-testid="video-list-tile"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onMouseEnter={() => !isMobile && setShowMenu(true)}
        onMouseLeave={() => !isMobile && !isDropdownOpen && setShowMenu(false)}
        onClick={handleSelectionClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelectionClick(e as any);
          }
        }}
        className={`
        group relative transition-colors cursor-pointer
        ${isSelected && selectMode ? 'bg-muted/50' : ''}
        ${showMenu && !selectMode ? 'bg-muted/30' : 'hover:bg-muted/20'}
      `}
        data-state={isSelected ? 'selected' : undefined}
      >
        {/* Name Column */}
        <TableCell className="h-14 w-[45%] min-w-[250px] pl-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              {isLoading && thumbnailSrc !== thumbUrl && (
                <Skeleton className="h-8 w-8 rounded absolute inset-0 bg-white dark:bg-black" />
              )}
              <img
                key={_id}
                src={thumbnailSrc}
                alt=""
                loading="eager"
                decoding="async"
                className={`h-8 w-8 rounded object-cover flex-shrink-0 ${
                  thumbnailSrc === DefaultThumbnail ? 'dark:invert' : ''
                } ${isLoading && thumbnailSrc !== thumbUrl ? 'opacity-0' : 'opacity-100'}`}
                onLoad={handleLoad}
                onError={handleError}
              />
              {selectMode && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[2px] rounded">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={handleCheckboxChange}
                    className="h-4 w-4"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              {selectMode ? (
                <span
                  className="text-sm font-medium truncate block w-full"
                  title={name}
                >
                  {name}
                </span>
              ) : (
                <a
                  href={fileUrl}
                  className="text-sm font-medium text-foreground hover:text-foreground/70 transition-colors truncate block w-full"
                  onClick={(e) => e.stopPropagation()}
                  title={name}
                >
                  {name}
                </a>
              )}
            </div>
          </div>
        </TableCell>

        {/* Duration Column */}
        <TableCell className="text-muted-foreground w-[70px] text-center">
          <span className="text-xs">{formatDuration(duration)}</span>
        </TableCell>

        {/* Folder Column (Recents view only) */}
        {isInRecentsView && (
          <TableCell className="hidden sm:table-cell w-[100px] text-center">
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1 font-normal text-muted-foreground hover:text-foreground hover:bg-transparent mx-auto"
              onClick={handleMoveClick}
            >
              <div className="flex items-center min-w-0">
                {parentId ? (
                  <>
                    <Users className="mr-1 h-3 w-3 flex-shrink-0" />
                    <span
                      className="truncate text-xs max-w-[60px]"
                      title={parentFolder?.name || 'Unknown Folder'}
                    >
                      {parentFolder?.name || 'Unknown Folder'}
                    </span>
                  </>
                ) : (
                  <>
                    <Lock className="mr-1 h-3 w-3 flex-shrink-0" />
                    <span className="text-xs">Private</span>
                  </>
                )}
              </div>
            </Button>
          </TableCell>
        )}

        {/* Creator Column */}
        <TableCell className="hidden md:table-cell text-muted-foreground w-[80px] text-center">
          <span
            className="truncate text-xs block max-w-full px-2"
            title={ownerName}
          >
            {ownerName}
          </span>
        </TableCell>

        {/* Date Column */}
        <TableCell className="text-muted-foreground w-[60px] text-center">
          <span className="text-xs">
            {moment(createdAt).format('DD/MM/YY')}
          </span>
        </TableCell>

        {/* Action Menu Column */}
        <TableCell className="w-[44px]">
          <div className="flex justify-end">
            <div
              className={`transition-opacity duration-150 ${showMenu || isMobile ? 'opacity-100' : 'opacity-0'}`}
            >
              <FileActionMenu file={file} onOpenChange={setIsDropdownOpen} />
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for memo - only re-render if these props change
    return (
      prevProps.file._id === nextProps.file._id &&
      prevProps.file.thumbUrl === nextProps.file.thumbUrl &&
      prevProps.file.type === nextProps.file.type &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.selectMode === nextProps.selectMode &&
      prevProps.fileUrl === nextProps.fileUrl
    );
  },
);

export default FileListItem;
